/**
 * FILE: WorkMotionV1.js
 * PURPOSE: Logic for SafeForm Occupational Ergonomics Interface.
 * VERSION: 1.0.5 (Fix: Robust Sentinel Filtering)
 */

// Load Chart.js (UMD)
const chartJsPromise = new Promise((resolve, reject) => {
    if (typeof Chart !== 'undefined') return resolve();
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js/dist/chart.umd.min.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Chart.js'));
    document.head.appendChild(script);
});

document.addEventListener('DOMContentLoaded', () => {
    'use strict';

    console.log("SafeForm WorkMotion UI Initializing...");

    const apiService = {
        // Reuse the same endpoints as the Sports App
        BASE_URL: 'https://55zydxbe05.execute-api.us-east-2.amazonaws.com',
        GET_PRESIGNED_URL: 'https://blt09ae2yd.execute-api.us-east-2.amazonaws.com/bighitterv6/get-presigned-url',
        VIDEO_URL: 'https://19tvyhtoch.execute-api.us-east-2.amazonaws.com/requestVideoFrameUrl',

        async getReports(cid, key, dates) {
            let url = `${this.BASE_URL}/reports?shopifyCustomerId=${cid}`;
            if (dates?.from) url += `&dateFrom=${dates.from}`;
            if (dates?.to) url += `&dateTo=${dates.to}`;
            if (key) url += `&exclusiveStartKey=${encodeURIComponent(JSON.stringify(key))}`;
            
            try {
                const res = await fetch(url);
                if (!res.ok) throw new Error(`API Error: ${res.status}`);
                return res.json();
            } catch (e) {
                console.error("Fetch failed:", e);
                return { items: [], lastEvaluatedKey: null };
            }
        },
        async getAnalysis(id, cid) {
            const res = await fetch(`${this.BASE_URL}/analysis/${id}?shopifyCustomerId=${cid}`);
            if (!res.ok) throw new Error("Failed to load analysis details");
            return res.json();
        },
        async getUploadUrl(file, meta) {
            const res = await fetch(this.GET_PRESIGNED_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filename: file, metadata: meta })
            });
            if (!res.ok) throw new Error("Failed to get upload URL");
            return res.json();
        },
        async getVideoUrl(s3Key) {
            const res = await fetch(this.VIDEO_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ file_name: s3Key })
            });
            if (!res.ok) throw new Error("Failed to get video URL");
            return res.json();
        }
    };

    const ui = {
        tabs: document.querySelectorAll('.wm-tab-trigger'),
        contents: document.querySelectorAll('.wm-tab-content'),
        
        init() {
            // Tab Switching Logic
            this.tabs.forEach(t => {
                t.addEventListener('click', (e) => {
                    e.preventDefault(); // Prevent default button behavior
                    const targetId = t.getAttribute('data-tab');
                    this.switchTab(targetId);
                });
            });

            // Date Filter
            const dateInput = document.getElementById("wmDateFilter");
            if (dateInput) {
                flatpickr(dateInput, { 
                    mode: "range", 
                    dateFormat: "Y-m-d", 
                    onClose: (d) => app.filterReports(d) 
                });
            }
        },
        
        switchTab(id) {
            // Toggle active class on buttons
            this.tabs.forEach(t => t.classList.toggle('active', t.getAttribute('data-tab') === id));
            
            // Toggle visibility of content
            this.contents.forEach(c => {
                if (c.id === id) {
                    c.style.display = 'block';
                    // Trigger data load if reports tab is clicked
                    if (id === 'wmReportsTab') app.loadReports(true);
                } else {
                    c.style.display = 'none';
                }
            });
        },
        
        showMsg(elId, msg, type) {
            const el = document.getElementById(elId);
            if (el) {
                el.textContent = msg;
                el.className = `wm-alert ${type}`; // 'success' or 'error'
                el.style.display = 'block';
            }
        }
    };

    const app = {
        customerId: window.wm_customerId,
        lastKey: null,
        isLoading: false,
        dateRange: null,
        activeCharts: {},

        init() {
            if (!this.customerId) return; 
            
            // Upload Form Handling
            const form = document.getElementById('wmScanForm');
            const fileInput = document.getElementById('wmVideoUpload');
            
            if (fileInput) {
                fileInput.addEventListener('change', () => {
                    const display = document.getElementById('wmVideoFilename');
                    if (display) display.textContent = fileInput.files[0]?.name || "No file selected.";
                });
            }

            if (form) {
                form.addEventListener('submit', async (e) => {
                    e.preventDefault();
                    if (!confirm("Start Scan? This may take several minutes.")) return;
                    
                    const btn = document.getElementById('wmSubmitBtn');
                    const progress = document.getElementById('wmUploadProgress');
                    const fill = document.getElementById('wmProgressBarFill');
                    const status = document.getElementById('wmStatusMessage');
                    
                    btn.disabled = true;
                    progress.style.display = 'block';
                    if (status) status.style.display = 'none';

                    try {
                        const file = fileInput.files[0];
                        if (!file) throw new Error("Please select a video file.");

                        const meta = {
                            shopifyCustomerId: this.customerId,
                            sport: 'occ_ergo', // CRITICAL TRIGGER
                            worker_id: form.worker_id.value || 'Unknown',
                            station_id: form.station_id.value || 'Unknown',
                            shift_code: form.shift_code.value || 'General'
                        };

                        const { presignedUrl } = await apiService.getUploadUrl(file.name, meta);
                        
                        // Upload
                        await new Promise((resolve, reject) => {
                            const xhr = new XMLHttpRequest();
                            xhr.open('PUT', presignedUrl, true);
                            xhr.upload.onprogress = (evt) => {
                                if (evt.lengthComputable) {
                                    const pct = (evt.loaded / evt.total) * 100;
                                    if (fill) fill.style.width = pct + '%';
                                    const txt = document.getElementById('wmProgressText');
                                    if (txt) txt.textContent = `Uploading... ${Math.round(pct)}%`;
                                }
                            };
                            xhr.onload = () => xhr.status === 200 ? resolve() : reject(`Upload failed: ${xhr.status}`);
                            xhr.onerror = () => reject('Network error during upload');
                            xhr.send(file);
                        });

                        ui.showMsg('wmStatusMessage', 'Scan Initiated. Check Reports shortly.', 'success');
                        form.reset();
                        document.getElementById('wmVideoFilename').textContent = "No file selected.";
                    } catch (err) {
                        ui.showMsg('wmStatusMessage', `Error: ${err.message || err}`, 'error');
                    } finally {
                        btn.disabled = false;
                        progress.style.display = 'none';
                    }
                });
            }

            // Reports Buttons
            const refreshBtn = document.getElementById('wmRefreshBtn');
            if (refreshBtn) refreshBtn.addEventListener('click', () => this.loadReports(true));
            
            const loadMoreBtn = document.getElementById('wmLoadMoreBtn');
            if (loadMoreBtn) loadMoreBtn.addEventListener('click', () => this.loadReports(false));
            
            // Video Modal Controls
            const closeVidBtn = document.getElementById('wmCloseVideoBtn');
            if (closeVidBtn) {
                closeVidBtn.addEventListener('click', () => {
                    document.getElementById('wmVideoModal').style.display = 'none';
                    const vid = document.getElementById('wmVideoElement');
                    if (vid) vid.pause();
                });
            }
        },

        async loadReports(reset) {
            if (this.isLoading) return;
            this.isLoading = true;
            
            const list = document.getElementById('wmReportsList');
            if (!list) return;

            if (reset) {
                list.innerHTML = '<div style="text-align:center; padding:20px; color:#888;">Loading Scans...</div>';
                this.lastKey = null;
            }

            try {
                const data = await apiService.getReports(this.customerId, this.lastKey, this.dateRange);
                
                // CLIENT-SIDE FILTER FOR ERGO ONLY
                const ergoItems = (data.items || []).filter(item => {
                    const triage = item.triageData ? (typeof item.triageData === 'string' ? JSON.parse(item.triageData) : item.triageData) : {};
                    const meta = item.playerMetadata ? (typeof item.playerMetadata === 'string' ? JSON.parse(item.playerMetadata) : item.playerMetadata) : {};
                    // Check both locations for safety
                    return (triage.determined_sport === 'occupational_ergonomics') || (meta.sport === 'occ_ergo');
                });

                if (reset) list.innerHTML = '';
                
                if (ergoItems.length === 0 && reset) {
                    list.innerHTML = '<div style="text-align:center; padding:20px;">No ergonomic scans found.</div>';
                } else {
                    ergoItems.forEach(item => {
                        const meta = item.playerMetadata ? (typeof item.playerMetadata === 'string' ? JSON.parse(item.playerMetadata) : item.playerMetadata) : {};
                        const date = item.creationTimestamp ? new Date(parseInt(item.creationTimestamp) * 1000).toLocaleString() : 'Unknown Date';
                        const statusClass = item.analysisStatus === 'COMPLETED' ? 'wm-status-completed' : 'wm-status-processing';
                        
                        const el = document.createElement('div');
                        el.className = 'wm-report-item';
                        el.innerHTML = `
                            <div>
                                <div class="wm-report-title">Shift Scan: ${meta.station_id || 'General'}</div>
                                <div class="wm-report-meta">
                                    <span>${date}</span>
                                    <span>Worker: ${meta.worker_id || 'N/A'}</span>
                                    <span class="wm-status-badge ${statusClass}">${item.analysisStatus || 'Pending'}</span>
                                </div>
                            </div>
                            <span class="material-symbols-outlined" style="color:var(--wm-accent-orange)">chevron_right</span>
                        `;
                        el.onclick = () => this.loadDetail(item.analysisId);
                        list.appendChild(el);
                    });
                }
                
                this.lastKey = data.lastEvaluatedKey;
                const lmBtn = document.getElementById('wmLoadMoreBtn');
                if (lmBtn) lmBtn.style.display = this.lastKey ? 'block' : 'none';

            } catch (e) {
                console.error(e);
                list.innerHTML = '<div class="wm-alert error">Failed to load reports.</div>';
            } finally {
                this.isLoading = false;
            }
        },

        async loadDetail(id) {
            const container = document.getElementById('wmReportDetailContainer');
            if (!container) return;

            container.style.display = 'block';
            container.innerHTML = '<div style="text-align:center; padding:20px;">Loading Detail...</div>';
            
            try {
                const data = await apiService.getAnalysis(id, this.customerId);
                const dossier = data.enrichedDataDossier ? (typeof data.enrichedDataDossier === 'string' ? JSON.parse(data.enrichedDataDossier) : data.enrichedDataDossier) : {};
                const s3Key = data.s3Key;

                await this.renderDashboard(container, dossier, s3Key);
                
                // Scroll to detail
                container.scrollIntoView({ behavior: 'smooth' });

            } catch (e) {
                container.innerHTML = `<div class="wm-alert error">${e.message}</div>`;
            }
        },

        async renderDashboard(container, dossier, videoKey) {
            if (!dossier || !dossier.action_list) {
                container.innerHTML = '<div class="wm-alert error">Data processing incomplete. Try again later.</div>';
                return;
            }

            // 1. Aggregate Stats
            const stats = {
                maxRula: 0,
                maxReba: 0,
                liftCount: 0,
                gaitIssues: 0
            };

            const events = [];
            const actions = dossier.action_list || [];
            
            actions.forEach(a => {
                // FIX: Robustly filter out any malformed rows or sentinel rows.
                if (!a || !a.time_range || a.action_key === 'SCAN_COMPLETE') return;

                const res = a.engine_results?.performance_metrics || {};
                
                // Collect Events for Table
                let riskVal = 0;
                let riskLabel = 'Low';
                let type = 'Unknown';

                if (a.action_key.includes('seated')) {
                    type = 'Seated (RULA)';
                    riskVal = res.rula_risk_score?.value || 0;
                    if (riskVal > stats.maxRula) stats.maxRula = riskVal;
                } else if (a.action_key.includes('standing')) {
                    type = 'Standing (REBA)';
                    riskVal = res.reba_risk_score?.value || 0;
                    if (riskVal > stats.maxReba) stats.maxReba = riskVal;
                } else if (a.action_key.includes('lift')) {
                    type = 'Lift Event';
                    stats.liftCount++;
                    // Custom Lift Risk Logic
                    riskVal = res.origin_height_in?.value < 30 ? 5 : 1; 
                } else if (a.action_key.includes('gait')) {
                    type = 'Gait Cycle';
                    const sym = res.gait_symmetry_ratio?.value;
                    if (sym < 0.9 || sym > 1.1) {
                        stats.gaitIssues++;
                        riskLabel = 'High (Asym)';
                    }
                }

                // Standardize Risk Color
                if (type.includes('RULA') || type.includes('REBA')) {
                    riskLabel = riskVal >= 6 ? 'High' : riskVal >= 3 ? 'Medium' : 'Low';
                }

                // Check if processed clip exists
                const clipKey = a.processed_clip?.s3_key || null;

                events.push({
                    time: a.time_range, // "5.5 - 8.5"
                    type: type,
                    risk: riskLabel,
                    val: riskVal,
                    // Parse start time for video player
                    startMs: parseFloat(a.time_range.split('-')[0]) * 1000,
                    processedClip: clipKey // Use processed clip if available
                });
            });

            // HTML Construction
            let html = `
                <div class="wm-risk-grid">
                    <div class="wm-risk-card ${this.getRiskClass(stats.maxRula, 7)}">
                        <div class="wm-risk-val">${stats.maxRula}</div>
                        <div class="wm-risk-label">Peak RULA Score</div>
                    </div>
                    <div class="wm-risk-card ${this.getRiskClass(stats.maxReba, 15)}">
                        <div class="wm-risk-val">${stats.maxReba}</div>
                        <div class="wm-risk-label">Peak REBA Score</div>
                    </div>
                    <div class="wm-risk-card low">
                        <div class="wm-risk-val">${stats.liftCount}</div>
                        <div class="wm-risk-label">Lift Events</div>
                    </div>
                    <div class="wm-risk-card ${stats.gaitIssues > 0 ? 'medium' : 'low'}">
                        <div class="wm-risk-val">${stats.gaitIssues}</div>
                        <div class="wm-risk-label">Gait Anomalies</div>
                    </div>
                </div>

                <div class="wm-card">
                    <h2 class="wm-card-header"><span class="material-symbols-outlined">list</span> Event Log</h2>
                    <table class="wm-event-table">
                        <thead>
                            <tr>
                                <th>Timestamp (s)</th>
                                <th>Event Type</th>
                                <th>Risk Level</th>
                                <th>Evidence</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${events.map((evt) => `
                                <tr>
                                    <td>${evt.time}</td>
                                    <td>${evt.type}</td>
                                    <td class="risk-${evt.risk.toLowerCase().split(' ')[0]}">${evt.risk}</td>
                                    <td>
                                        <button class="wm-button-icon" onclick="window.playWmClip('${evt.processedClip || videoKey}', ${evt.startMs}, ${!!evt.processedClip})">
                                            <span class="material-symbols-outlined">play_circle</span>
                                        </button>
                                    </td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
                
                <div class="wm-card">
                    <h2 class="wm-card-header"><span class="material-symbols-outlined">analytics</span> Risk Timeline</h2>
                    <div class="chart-container">
                        <canvas id="wmRiskChart"></canvas>
                    </div>
                </div>
            `;

            container.innerHTML = html;

            // Render Chart
            await this.renderChart(events);
        },

        getRiskClass(val, max) {
            const pct = val / max;
            if (pct > 0.7) return 'high';
            if (pct > 0.4) return 'medium';
            return 'low';
        },

        async renderChart(events) {
            try {
                await chartJsPromise; // Now valid inside async function
                
                const canvas = document.getElementById('wmRiskChart');
                if (!canvas) return;
                
                const ctx = canvas.getContext('2d');
                
                // Map events to data points
                const dataPoints = events.map(e => ({
                    x: parseFloat(e.time.split('-')[0]),
                    y: e.type.includes('RULA') ? e.val : (e.type.includes('REBA') ? e.val / 1.5 : 0),
                    label: e.type
                })).sort((a,b) => a.x - b.x);

                if (app.activeCharts?.risk) app.activeCharts.risk.destroy();

                app.activeCharts.risk = new Chart(ctx, {
                    type: 'line',
                    data: {
                        datasets: [{
                            label: 'Postural Risk Score (Normalized)',
                            data: dataPoints,
                            borderColor: '#ff9f43',
                            backgroundColor: 'rgba(255, 159, 67, 0.2)',
                            pointRadius: 6,
                            pointHoverRadius: 8
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: { type: 'linear', title: { display: true, text: 'Time (seconds)' } },
                            y: { min: 0, max: 10, title: { display: true, text: 'Risk Severity' } }
                        },
                        plugins: {
                            tooltip: {
                                callbacks: {
                                    label: (ctx) => `${ctx.raw.label}: ${ctx.raw.y}`
                                }
                            }
                        }
                    }
                });
            } catch (e) {
                console.warn("Chart rendering failed:", e);
            }
        },

        filterReports(dates) {
            this.dateRange = { 
                from: dates[0]?.toISOString().split('T')[0], 
                to: dates[1]?.toISOString().split('T')[0] 
            };
            this.loadReports(true);
        }
    };

    // Global Player Function
    window.playWmClip = async (key, startMs, isProcessed) => {
        const modal = document.getElementById('wmVideoModal');
        const video = document.getElementById('wmVideoElement');
        const loading = document.getElementById('wmVideoLoading');
        
        modal.style.display = 'flex';
        video.style.display = 'none';
        loading.style.display = 'block';
        
        try {
            const res = await apiService.getVideoUrl(key);
            video.src = res.presigned_url;
            
            // Only jump to timestamp if it's the RAW video. 
            // Processed clips are already trimmed.
            if (!isProcessed) {
                video.currentTime = startMs / 1000;
            }
            
            video.oncanplay = () => {
                loading.style.display = 'none';
                video.style.display = 'block';
                video.play();
            };
        } catch (e) {
            loading.innerText = "Error loading video.";
        }
    };

    ui.init();
    app.init();
});